import React, { useEffect, useRef, useState } from 'react';
import './VerificationGate.css';

/* One-time verification gate.
   ---------------------------
   Wraps `<App />` in index.js. Before the user has answered the
   verification question correctly, this component renders a full-
   screen black overlay with the question (English + Russian) and an
   input + OK button. Once the correct answer is entered, a flag is
   written to localStorage and `children` (the actual app) renders
   instead — that flag is checked on every subsequent visit, so the
   gate never appears again on that device.
   
   Answer matching is case-insensitive AND whitespace-insensitive:
   both the user input and the expected answer are lower-cased and
   stripped of ALL whitespace before comparing. So "You look so
   pretty", "youlooksopretty", "YOU  LOOK SO PRETTY ", etc. all pass.
*/

const STORAGE_KEY = 'verif_gate_v1';
const EXPECTED_RAW = 'You look so pretty';

// Normalisation: lowercase + remove ALL whitespace (spaces, tabs,
// newlines, etc.). The expected answer is normalised at module load
// so we don't recompute on every keystroke.
const normalise = (s) => (s || '').toLowerCase().replace(/\s+/g, '');
const EXPECTED_NORM = normalise(EXPECTED_RAW);

const isVerified = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (_e) {
    return false;
  }
};

const markVerified = () => {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch (_e) {
    /* no-op — verification just won't persist if storage is blocked */
  }
};

export const VerificationGate = ({ children }) => {
  // Synchronous initial check so the gate doesn't flash before
  // unmounting for an already-verified user.
  const [verified, setVerified] = useState(isVerified);
  const [value, setValue] = useState('');
  const [wrong, setWrong] = useState(false);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  // Auto-focus the input on mount so the keyboard pops up immediately
  // on mobile — the user can answer without an extra tap.
  useEffect(() => {
    if (verified) return undefined;
    const t = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 80);
    return () => clearTimeout(t);
  }, [verified]);

  // Clear the shake-animation class after it finishes so it can replay
  // on the next wrong attempt.
  useEffect(() => {
    if (!wrong) return undefined;
    const t = setTimeout(() => setWrong(false), 320);
    return () => clearTimeout(t);
  }, [wrong]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (normalise(value) === EXPECTED_NORM) {
      markVerified();
      setVerified(true);
    } else {
      setWrong(true);
    }
  };

  if (verified) {
    return children;
  }

  return (
    <div className="verif-gate" data-testid="verification-gate" role="dialog" aria-modal="true">
      <form
        ref={panelRef}
        className={`verif-gate__panel${wrong ? ' is-wrong' : ''}`}
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <p className="verif-gate__question" data-testid="verification-question-en">
          What is the caption of the video you send me all the time? (English, no emojis)
        </p>

        <hr className="verif-gate__rule" aria-hidden="true" />

        <p className="verif-gate__question" data-testid="verification-question-ru">
          Какая подпись у видео, которое ты мне всегда отправляешь? (на английском, без эмодзи)
        </p>

        <input
          ref={inputRef}
          type="text"
          className="verif-gate__input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="go"
          data-testid="verification-input"
          aria-label="answer"
        />

        <div className="verif-gate__error" aria-live="polite">
          {wrong ? 'try again' : ''}
        </div>

        <button
          type="submit"
          className="verif-gate__button"
          disabled={value.trim().length === 0}
          data-testid="verification-submit"
        >
          ok
        </button>
      </form>
    </div>
  );
};

export default VerificationGate;
